// Replaces YouWare's hosted `client.auth.renderAuthUI(...)` widget (see the
// old AppLayout.tsx) — a small, plain login form calling better-auth's
// sign-in endpoint directly. Kept intentionally simple; this is the one
// piece of UI that genuinely needed to be built from scratch for this
// migration, since a YouWare-hosted widget has no equivalent to carry over.
import { useState } from "react";
import { authClient } from "../../lib/authClient";

interface LoginFormProps {
  onLogin: () => void | Promise<void>;
}

export function LoginForm({ onLogin }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Sign-in for a user with 2FA enabled doesn't return a session right away —
  // better-auth responds with `{twoFactorRedirect: true, twoFactorMethods}`
  // instead (see auth-config.ts's twoFactor() plugin). Once that happens we
  // stay on this same form and switch to a second step asking for the code,
  // rather than navigating anywhere — the pending challenge is tracked by a
  // signed cookie better-auth already set, so no extra state to carry here.
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data, error: signInError } = await authClient.signIn.email({ email, password });
      if (signInError) {
        setError(signInError.message || "Invalid email or password.");
        return;
      }
      if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
        setNeedsTwoFactor(true);
        return;
      }
      await onLogin();
    } catch (err) {
      console.error("Login failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setVerifying(true);
    try {
      const { error: verifyError } = useBackupCode
        ? await authClient.twoFactor.verifyBackupCode({ code })
        : await authClient.twoFactor.verifyTotp({ code });
      if (verifyError) {
        // "Invalid two factor cookie" means the short-lived cookie that
        // links this code back to the sign-in attempt is missing or
        // expired server-side (see auth-config.ts's twoFactorCookieMaxAge
        // comment) — the code itself was never actually checked. Staying
        // on this screen just lets the user retype a code that can never
        // succeed, so send them back to sign-in with a clear reason
        // instead of a raw library error string.
        if (verifyError.message?.toLowerCase().includes("two factor cookie")) {
          setError("Your verification session expired. Please sign in again.");
          setNeedsTwoFactor(false);
          setCode("");
          return;
        }
        setError(verifyError.message || "That code didn't work. Please try again.");
        return;
      }
      await onLogin();
    } catch (err) {
      console.error("2FA verification failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  if (needsTwoFactor) {
    return (
      <div className="w-full max-w-[420px] rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-gray-900">Two-factor verification</h1>
        <p className="mb-6 text-sm text-gray-500">
          {useBackupCode
            ? "Enter one of your unused backup codes."
            : "Enter the 6-digit code from your authenticator app."}
        </p>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label htmlFor="code" className="mb-1 block text-sm font-medium text-gray-700">
              {useBackupCode ? "Backup code" : "Authentication code"}
            </label>
            <input
              id="code"
              type="text"
              inputMode={useBackupCode ? "text" : "numeric"}
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm tracking-widest focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={useBackupCode ? "xxxxxxxx" : "123456"}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={verifying || !code}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {verifying ? "Verifying…" : "Verify"}
          </button>

          <div className="flex justify-between text-xs text-gray-500">
            <button
              type="button"
              onClick={() => {
                setUseBackupCode((v) => !v);
                setCode("");
                setError(null);
              }}
              className="hover:text-blue-600 hover:underline"
            >
              {useBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
            </button>
            <button
              type="button"
              onClick={() => {
                setNeedsTwoFactor(false);
                setCode("");
                setError(null);
              }}
              className="hover:text-blue-600 hover:underline"
            >
              Back to sign in
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[420px] rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
      <h1 className="mb-1 text-xl font-semibold text-gray-900">EnergiPro IMS</h1>
      <p className="mb-6 text-sm text-gray-500">Sign in to continue</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <div className="flex justify-between text-xs text-gray-500">
          <a href="/reset-password" className="hover:text-blue-600 hover:underline">
            Forgot password?
          </a>
        </div>
      </form>
    </div>
  );
}
